import { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { Search, Trash2, Loader2, AlertCircle, Plus, Edit } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

export default function AdminArticles() {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleting, setIsDeleting] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(collection(db, 'articles'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const articlesData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        let dateStr = 'غير معروف';
        if (data.createdAt) {
          const d = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
          dateStr = d.toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' });
        }
        return {
          id: doc.id,
          ...data,
          dateStr,
        };
      });
      setArticles(articlesData);
      setFilteredArticles(articlesData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching articles:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Search Logic
  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const filtered = articles.filter(article =>
      (article.title && article.title.toLowerCase().includes(term)) ||
      (article.authorName && article.authorName.toLowerCase().includes(term)) ||
      (article.category && article.category.toLowerCase().includes(term))
    );
    setFilteredArticles(filtered);
  }, [searchTerm, articles]);

  // Handle Delete
  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه المقالة نهائياً؟ لا يمكن التراجع عن هذا الإجراء.')) return;

    setIsDeleting(id);
    try {
      await deleteDoc(doc(db, 'articles', id));
      setArticles(prev => prev.filter(article => article.id !== id));
    } catch (error) {
      console.error("Error deleting document:", error);
      alert('حدث خطأ أثناء الحذف.');
    } finally {
      setIsDeleting(null);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-2">إدارة المقالات</h1>
          <p className="text-slate-500 dark:text-zinc-400">إدارة مقالات المدونة، إضافة، تعديل، وحذف المقالات.</p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Link
            to="/admin/articles/new"
            className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl font-bold transition-all shadow-sm"
          >
            <Plus className="w-5 h-5" />
            <span>كتابة مقالة</span>
          </Link>
        </div>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl overflow-hidden shadow-sm">
        {/* Toolbar */}
        <div className="p-4 md:p-6 border-b border-slate-200 dark:border-white/10 flex flex-col sm:flex-row gap-4 items-center justify-between bg-slate-50/50 dark:bg-zinc-900/50">
          <div className="relative w-full sm:max-w-md">
            <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
              <Search className="w-5 h-5 text-slate-400" />
            </div>
            <input
              type="text"
              placeholder="ابحث بالعنوان، الكاتب، أو الفئة..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-11 py-3 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all text-slate-900 dark:text-white"
            />
          </div>
          <div className="text-lg font-bold text-slate-500 dark:text-zinc-400 whitespace-nowrap">
            إجمالي المقالات: <span className="text-primary-600 dark:text-primary-400">{filteredArticles.length}</span>
          </div>
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-500">
              <Loader2 className="w-10 h-10 animate-spin text-primary-600 mb-4" />
              <p className="font-bold">جاري تحميل البيانات...</p>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-500">
              <AlertCircle className="w-12 h-12 text-slate-300 dark:text-zinc-700 mb-4" />
              <p className="text-lg font-bold">لا يوجد مقالات تطابق بحثك</p>
            </div>
          ) : (
            <table className="w-full text-right border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-zinc-900 border-b border-slate-200 dark:border-white/10 text-slate-500 dark:text-zinc-400 text-lg font-bold">
                  <th className="px-6 py-4 whitespace-nowrap">الصورة</th>
                  <th className="px-6 py-4 whitespace-nowrap w-1/3">العنوان</th>
                  <th className="px-6 py-4 whitespace-nowrap">الفئة</th>
                  <th className="px-6 py-4 whitespace-nowrap">تاريخ النشر</th>
                  <th className="px-6 py-4 whitespace-nowrap">الإعجابات</th>
                  <th className="px-6 py-4 whitespace-nowrap text-center">إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {filteredArticles.map((article) => (
                  <tr key={article.id} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors group">
                    <td className="px-6 py-5 whitespace-nowrap">
                      {article.coverImageUrl ? (
                        <div className="w-16 h-12 rounded-lg bg-cover bg-center" style={{ backgroundImage: `url('${article.coverImageUrl}')` }} />
                      ) : (
                        <div className="w-16 h-12 rounded-lg bg-slate-200 dark:bg-slate-800 flex items-center justify-center text-md text-slate-500">لا صورة</div>
                      )}
                    </td>
                    <td className="px-6 py-5 font-bold text-slate-900 dark:text-white min-w-[200px]">
                      {article.title}
                      <div className="text-mdfont-medium text-slate-500 mt-1">بواسطة: {article.authorName}</div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-md font-bold bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400">
                        {article.category}
                      </span>
                    </td>
                    <td className="px-6 py-5 text-lg text-slate-500 dark:text-zinc-400 font-medium whitespace-nowrap">
                      {article.dateStr}
                    </td>
                    <td className="px-6 py-5 text-lg font-bold text-slate-700 dark:text-zinc-300 whitespace-nowrap">
                      {article.likesCount || 0} ❤️
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2 opacity-100 lg:opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => navigate(`/admin/articles/edit/${article.id}`)}
                          className="p-2 text-slate-400 hover:text-blue-600 bg-slate-100 hover:bg-blue-50 dark:bg-white/5 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
                          title="تعديل"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(article.id)}
                          disabled={isDeleting === article.id}
                          className="p-2 text-slate-400 hover:text-red-600 bg-slate-100 hover:bg-red-50 dark:bg-white/5 dark:hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                          title="حذف المقالة"
                        >
                          {isDeleting === article.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
